import {
  compareTimes,
  isStartBeforeEnd,
  isValid12h,
  normalize12h,
  to12h,
} from './timeFormat'

export type TimeOptionKind = 'open' | 'close'

export interface TimeOption {
  value: string
  label: string
}

const STEP_MINUTES = 15
// Lets a store close at end of day: "12:00 AM" (00:00) can never be after the
// opening time, so the close list ends with 11:59 PM (`23:59` on the wire).
const END_OF_DAY = '11:59 PM'

function generate(kind: TimeOptionKind): TimeOption[] {
  const options: TimeOption[] = []
  for (let minutes = 0; minutes < 24 * 60; minutes += STEP_MINUTES) {
    const hh = String(Math.floor(minutes / 60)).padStart(2, '0')
    const mm = String(minutes % 60).padStart(2, '0')
    const label = to12h(`${hh}:${mm}`)
    options.push({ value: label, label })
  }
  if (kind === 'close') {
    options.push({ value: END_OF_DAY, label: END_OF_DAY })
  }
  return options
}

const OPTIONS: Record<TimeOptionKind, TimeOption[]> = {
  open: generate('open'),
  close: generate('close'),
}

export function buildTimeOptions(kind: TimeOptionKind): TimeOption[] {
  return OPTIONS[kind]
}

// Saved values that are not on the 15-minute grid (e.g. "9:05 AM") are added
// in sorted order so they still display and round-trip unchanged.
export function withCurrentValue(
  options: TimeOption[],
  value: string,
): TimeOption[] {
  if (!value || !isValid12h(value)) return options
  const normalized = normalize12h(value)
  if (options.some((o) => o.value === normalized)) return options
  const index = options.findIndex((o) => compareTimes(o.value, normalized) > 0)
  const extra = { value: normalized, label: normalized }
  if (index === -1) return [...options, extra]
  return [...options.slice(0, index), extra, ...options.slice(index)]
}

// True only when both times are complete and the end is not after the start.
// Used to flag the closing select; incomplete values are left to the
// editors' own "invalid format" handling.
export function isRangeInverted(start: string, end: string): boolean {
  return isValid12h(start) && isValid12h(end) && !isStartBeforeEnd(start, end)
}
